const SIZE_CHART_ID = 'SwaglabSizeChart';

function normalizeSizeChart(chart, { modal = false } = {}) {
  const unitButtons = [...chart.querySelectorAll('button')].filter((button) => {
    const label = button.textContent.trim().toLowerCase();
    return button.classList.contains('size-unit-btn')
      || /^(cm|metric)\b/.test(label)
      || label.includes('kg')
      || /^(in|inch|inches|imperial)\b/.test(label)
      || label.includes('ft')
      || label.includes('lb');
  });

  unitButtons.forEach((button, index) => {
    const label = button.textContent.trim().toLowerCase();
    const isMetric = /^(cm|metric)\b/.test(label) || label.includes('kg') || button.dataset.unit === 'cm';
    button.classList.add('size-unit-btn');
    button.dataset.unit = isMetric ? 'cm' : 'in';
    button.textContent = isMetric ? 'CM / KG' : 'IN / FT / LB';
    button.setAttribute('type', 'button');
    if (!unitButtons.some((candidate) => candidate.classList.contains('active')) && index === 0) {
      button.classList.add('active');
    }
    button.setAttribute('aria-pressed', button.classList.contains('active') ? 'true' : 'false');
  });

  if (unitButtons.length > 1 && unitButtons.every((button) => button.parentElement === unitButtons[0].parentElement)) {
    unitButtons[0].parentElement?.classList.add('swaglab-size-guide__units');
  }

  chart.querySelectorAll('tr').forEach((row) => {
    const cells = [...row.querySelectorAll('th, td')];
    if (cells.length < 2) return;

    const rowLabel = cells[0].textContent.trim().toLowerCase();
    const measure = /height|stature/.test(rowLabel)
      ? 'height'
      : /weight|body\s*mass/.test(rowLabel)
        ? 'weight'
        : 'length';

    cells.slice(1).forEach((cell) => {
      const metricValue = cell.dataset.cm || cell.dataset.swaglabMetric || cell.textContent.trim();
      if (!/\d/.test(metricValue)) return;
      cell.dataset.swaglabMetric = metricValue;
      cell.dataset.swaglabMeasure = measure;
    });
  });

  if (modal) {
    const heading = chart.querySelector('h1, h2, h3, h4');
    if (heading && /size\s*(chart|guide)/i.test(heading.textContent)) {
      heading.classList.add('swaglab-size-guide__source-title');
    }
  }
}

function prepareSizeChartLinks(root = document) {
  const charts = [...root.querySelectorAll('.size-chart-wrapper')];
  const links = document.querySelectorAll('[data-swaglab-size-chart-link]');

  charts.forEach((chart, index) => {
    chart.id = index === 0 ? SIZE_CHART_ID : `${SIZE_CHART_ID}-${index + 1}`;
    chart.classList.add('swaglab-unified-size-chart');
    chart.setAttribute('tabindex', '-1');
    normalizeSizeChart(chart);
  });

  links.forEach((link) => {
    const chart = charts[0] || document.querySelector('.size-chart-wrapper');
    link.hidden = !chart;
    if (chart) link.setAttribute('href', `#${chart.id}`);
  });
}

function getSizeGuideDialog() {
  return document.querySelector('[data-swaglab-size-guide]');
}

function openSizeGuide(chart, trigger) {
  const dialog = getSizeGuideDialog();
  const content = dialog?.querySelector('[data-swaglab-size-guide-content]');
  if (!dialog || !content || !chart) return;
  if (dialog.open) closeSizeGuide(dialog);

  const chartClone = chart.cloneNode(true);
  chartClone.removeAttribute('id');
  chartClone.removeAttribute('tabindex');
  chartClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
  normalizeSizeChart(chartClone, { modal: true });

  content.replaceChildren(chartClone);
  const activeUnit = chartClone.querySelector('.size-unit-btn.active, .size-unit-btn[aria-pressed="true"]')
    || chartClone.querySelector('.size-unit-btn');
  if (activeUnit) updateSizeChartUnit(activeUnit);
  dialog.swaglabTrigger = trigger;
  dialog.showModal();
  document.body.classList.add('swaglab-overlay-open');
  dialog.querySelector('[data-swaglab-size-guide-close]')?.focus();
}

function closeSizeGuide(dialog = getSizeGuideDialog()) {
  if (!dialog?.open) return;
  dialog.close();
  document.body.classList.remove('swaglab-overlay-open');
  dialog.swaglabTrigger?.focus?.();
}

function updateSizeChartUnit(button) {
  const chart = button.closest('.size-chart-wrapper');
  if (!chart) return;

  const unit = button.dataset.unit;
  if (!unit) return;

  chart.querySelectorAll('.size-unit-btn').forEach((unitButton) => {
    unitButton.classList.toggle('active', unitButton === button);
    unitButton.setAttribute('aria-pressed', unitButton === button ? 'true' : 'false');
  });

  const formatRange = (rawValue, formatter, suffix = '') => {
    const normalized = rawValue.replace(/,/g, '.').trim();
    const values = [...normalized.matchAll(/\d+(?:\.\d+)?/g)].map((match) => Number.parseFloat(match[0]));
    if (!values.length) return rawValue;

    const converted = values.map(formatter);
    const isRange = values.length > 1;
    const isOpenEnded = values.length === 1 && /[-–—]\s*(?:kg|kgs|cm)?\s*$/i.test(normalized);
    if (isRange) return `${converted[0]}–${converted[1]}${suffix}`;
    if (isOpenEnded) return `${converted[0]}+${suffix}`;
    return `${converted[0]}${suffix}`;
  };

  const cmToFeet = (centimeters) => {
    const totalInches = Math.round(centimeters / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}′${inches}″`;
  };

  chart.querySelectorAll('[data-swaglab-metric], [data-cm]').forEach((cell) => {
    const metricValue = cell.dataset.swaglabMetric || cell.dataset.cm || '';
    const measure = cell.dataset.swaglabMeasure || 'length';
    if (unit === 'cm') {
      cell.textContent = metricValue;
      return;
    }

    if (measure === 'height') {
      cell.textContent = formatRange(metricValue, cmToFeet);
    } else if (measure === 'weight') {
      cell.textContent = formatRange(metricValue, (kilograms) => Math.round(kilograms * 2.20462), ' lb');
    } else {
      cell.textContent = formatRange(metricValue, (centimeters) => (centimeters / 2.54).toFixed(1));
    }
  });
}

function collectionLabelFromHandle(handle = '') {
  return decodeURIComponent(handle)
    .split('-')
    .filter(Boolean)
    .map((word) => {
      if (word.toLowerCase() === 'mens') return "Men's";
      if (word.toLowerCase() === 'womens') return "Women's";
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function prepareProductOrigin(root = document) {
  const origins = root.matches?.('[data-swaglab-product-origin]')
    ? [root]
    : [...root.querySelectorAll('[data-swaglab-product-origin]')];
  if (!origins.length) return;

  let source = null;
  try {
    const referrer = document.referrer ? new URL(document.referrer) : null;
    const match = referrer?.origin === window.location.origin
      ? referrer.pathname.match(/^\/collections\/([^/]+)/)
      : null;
    if (match?.[1]) {
      source = {
        href: `/collections/${match[1]}`,
        label: collectionLabelFromHandle(match[1]),
      };
    } else {
      const stored = JSON.parse(window.sessionStorage.getItem('swaglab-product-origin') || 'null');
      if (stored?.href && stored?.label && Date.now() - stored.savedAt < 30 * 60 * 1000) source = stored;
    }
  } catch (_error) {
    // Referrer and session storage can be unavailable in strict privacy modes.
  }

  origins.forEach((origin) => {
    const sourceLink = origin.querySelector('[data-swaglab-product-origin-link]');
    const separator = origin.querySelector('[data-swaglab-product-origin-separator]');
    if (!source || !sourceLink) {
      sourceLink?.setAttribute('hidden', '');
      separator?.setAttribute('hidden', '');
      return;
    }
    sourceLink.href = source.href;
    sourceLink.textContent = source.label;
    sourceLink.removeAttribute('hidden');
    separator?.removeAttribute('hidden');
  });
}

function rememberCollectionOrigin() {
  const match = window.location.pathname.match(/^\/collections\/([^/]+)/);
  if (!match?.[1]) return;
  try {
    window.sessionStorage.setItem('swaglab-product-origin', JSON.stringify({
      href: `/collections/${match[1]}`,
      label: collectionLabelFromHandle(match[1]),
      savedAt: Date.now(),
    }));
  } catch (_error) {
    // The breadcrumb still falls back to HOME · PRODUCT when storage is unavailable.
  }
}

function prepareProductGalleryPagination(root = document) {
  const selector = 'media-gallery slideshow-component';
  const slideshows = root.matches?.(selector)
    ? [root]
    : [...root.querySelectorAll(selector)];

  slideshows.forEach((slideshow) => {
    const scroller = slideshow.querySelector('slideshow-slides[ref="scroller"]');
    const nativeDots = slideshow.querySelector('.media-gallery__mobile-controls .slideshow-controls__dots');
    const nativeButtons = nativeDots ? [...nativeDots.querySelectorAll('.slideshow-control')] : [];
    if (!scroller || !nativeDots || nativeButtons.length < 2) return;

    const controls = nativeDots.closest('.media-gallery__mobile-controls');
    if (!controls) return;
    if (
      slideshow.dataset.swaglabPaginationReady === 'true'
      && controls.querySelector('.swaglab-gallery-pagination')
    ) {
      return;
    }

    controls
      .querySelectorAll('.swaglab-gallery-scrubber, .swaglab-gallery-progress, .swaglab-gallery-pagination')
      .forEach((element) => element.remove());
    nativeDots.classList.remove('swaglab-gallery-dots--native');
    nativeDots.classList.add('swaglab-gallery-native-dots');

    const pagination = document.createElement('div');
    pagination.className = 'swaglab-gallery-pagination';
    pagination.style.setProperty('--swaglab-gallery-count', nativeButtons.length);

    const track = document.createElement('div');
    track.className = 'swaglab-gallery-pagination__track';
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', 'Product images');

    const markers = [];
    nativeButtons.forEach((nativeButton, index) => {
      const marker = document.createElement('button');
      marker.type = 'button';
      marker.className = 'swaglab-gallery-pagination__dot';
      marker.setAttribute('aria-label', `View product image ${index + 1} of ${nativeButtons.length}`);
      marker.addEventListener('click', () => nativeButton.click());
      markers.push(marker);
      track.append(marker);
    });

    const thumb = document.createElement('span');
    thumb.className = 'swaglab-gallery-pagination__active';
    thumb.setAttribute('aria-hidden', 'true');
    track.append(thumb);
    pagination.append(track);
    controls.append(pagination);

    let animationFrame = 0;

    const updatePagination = () => {
      animationFrame = 0;
      const slides = [...scroller.querySelectorAll(':scope > slideshow-slide')].slice(0, markers.length);
      const selectedIndex = nativeButtons.findIndex(
        (dot) => dot.getAttribute('aria-selected') === 'true'
      );

      let progress = selectedIndex < 0 ? 0 : selectedIndex;
      if (slides.length === markers.length && slides.length > 1) {
        const viewportCenter = Math.abs(scroller.scrollLeft) + scroller.clientWidth / 2;
        const slideCenters = slides.map((slide) => slide.offsetLeft + slide.offsetWidth / 2);

        if (viewportCenter <= slideCenters[0]) {
          progress = 0;
        } else if (viewportCenter >= slideCenters[slideCenters.length - 1]) {
          progress = slides.length - 1;
        } else {
          for (let index = 0; index < slideCenters.length - 1; index += 1) {
            const start = slideCenters[index];
            const end = slideCenters[index + 1];
            if (viewportCenter >= start && viewportCenter <= end) {
              progress = index + (viewportCenter - start) / Math.max(1, end - start);
              break;
            }
          }
        }
      }
      progress = Math.max(0, Math.min(markers.length - 1, progress));

      const lowerIndex = Math.floor(progress);
      const upperIndex = Math.min(markers.length - 1, lowerIndex + 1);
      const fraction = progress - lowerIndex;
      const lowerCenter = markers[lowerIndex].offsetLeft + markers[lowerIndex].offsetWidth / 2;
      const upperCenter = markers[upperIndex].offsetLeft + markers[upperIndex].offsetWidth / 2;
      const indicatorCenter = lowerCenter + (upperCenter - lowerCenter) * fraction;
      pagination.style.setProperty('--swaglab-gallery-pagination-x', `${indicatorCenter.toFixed(3)}px`);

      const currentIndex = Math.round(progress);
      markers.forEach((marker, index) => {
        if (index === currentIndex) marker.setAttribute('aria-current', 'true');
        else marker.removeAttribute('aria-current');
      });
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updatePagination);
    };

    scroller.addEventListener('scroll', scheduleUpdate, { passive: true });
    slideshow.addEventListener('slideshow:select', scheduleUpdate);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(scroller);
      resizeObserver.observe(pagination);
      slideshow.swaglabPaginationResizeObserver = resizeObserver;
    }

    delete slideshow.dataset.swaglabProgressReady;
    slideshow.dataset.swaglabPaginationReady = 'true';
    window.requestAnimationFrame(() => window.requestAnimationFrame(updatePagination));
  });
}

function prepareProductLightbox(root = document) {
  const selector = 'main[data-template="product"] zoom-dialog, zoom-dialog';
  const lightboxes = root.matches?.('zoom-dialog')
    ? [root]
    : [...root.querySelectorAll(selector)];

  lightboxes.forEach((lightbox) => {
    if (lightbox.dataset.swaglabLightboxReady === 'true') return;

    const dialog = lightbox.querySelector('.dialog-zoomed-gallery__dialog');
    const gallery = lightbox.querySelector('.dialog-zoomed-gallery');
    const thumbnails = [...lightbox.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
    const media = [...lightbox.querySelectorAll('.dialog-zoomed-gallery > .product-media-container')];
    if (!dialog || !gallery || !thumbnails.length || !media.length) return;

    let activeIndex = 0;
    let animationFrame = 0;

    const counter = document.createElement('div');
    counter.className = 'swaglab-lightbox__counter';
    counter.setAttribute('aria-live', 'polite');

    const controls = document.createElement('div');
    controls.className = 'swaglab-lightbox__controls';

    const createArrow = (direction, label, path) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `swaglab-lightbox__arrow swaglab-lightbox__arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${path}" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      return button;
    };

    const previous = createArrow('previous', 'Previous product image', 'M14.5 5 7.5 12l7 7');
    const next = createArrow('next', 'Next product image', 'm9.5 5 7 7-7 7');
    controls.append(previous, next);
    dialog.append(counter, controls);

    const render = (index = activeIndex) => {
      activeIndex = Math.max(0, Math.min(media.length - 1, index));
      counter.textContent = `${activeIndex + 1} / ${media.length}`;
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === media.length - 1;
    };

    const inferVisibleIndex = () => {
      if (!dialog.open) return activeIndex;
      const viewportCenter = window.innerWidth / 2;
      let bestIndex = activeIndex;
      let bestDistance = Number.POSITIVE_INFINITY;
      media.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        if (rect.width === 0) return;
        const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });
      return bestIndex;
    };

    const scheduleGeometryUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        render(inferVisibleIndex());
      });
    };

    const select = (index) => {
      const targetIndex = Math.max(0, Math.min(media.length - 1, index));
      if (typeof lightbox.handleThumbnailClick === 'function') {
        lightbox.handleThumbnailClick(targetIndex);
      } else {
        thumbnails[targetIndex]?.click();
      }
      render(targetIndex);
    };

    previous.addEventListener('click', () => select(activeIndex - 1));
    next.addEventListener('click', () => select(activeIndex + 1));
    lightbox.addEventListener('zoom-media:selected', (event) => render(event.detail?.index ?? inferVisibleIndex()));
    gallery.addEventListener('scroll', scheduleGeometryUpdate, { passive: true });
    dialog.addEventListener('scroll', scheduleGeometryUpdate, { passive: true });
    dialog.addEventListener('toggle', () => {
      if (dialog.open) window.requestAnimationFrame(scheduleGeometryUpdate);
    });

    const mediaGallery = lightbox.closest('media-gallery');
    mediaGallery?.addEventListener('click', (event) => {
      const zoomButton = event.target.closest('.product-media-container__zoom-button');
      const action = zoomButton?.getAttribute('on:click') || '';
      const index = Number.parseInt(action.match(/\/zoom\/(\d+)/)?.[1] || '', 10);
      if (Number.isFinite(index)) {
        activeIndex = index;
        window.requestAnimationFrame(() => render(index));
      }
    }, true);

    const selectionObserver = new MutationObserver(() => {
      const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-selected') === 'true');
      if (selectedIndex >= 0) render(selectedIndex);
    });
    thumbnails.forEach((thumbnail) => selectionObserver.observe(thumbnail, {
      attributes: true,
      attributeFilter: ['aria-selected'],
    }));

    lightbox.swaglabSelectionObserver = selectionObserver;
    lightbox.dataset.swaglabLightboxReady = 'true';
    render(0);
  });
}

function formatCartDestination(country) {
  return country || '';
}

function prepareCartTrust(root = document) {
  const closestBlock = root.closest?.('[data-swaglab-cart-trust]');
  const blocks = root.matches?.('[data-swaglab-cart-trust]')
    ? [root]
    : closestBlock
      ? [closestBlock]
      : [...root.querySelectorAll('[data-swaglab-cart-trust]')];

  blocks.forEach((block) => {
    const deliveryFrom = new Date();
    const deliveryTo = new Date();
    deliveryFrom.setDate(deliveryFrom.getDate() + 14);
    deliveryTo.setDate(deliveryTo.getDate() + 18);

    const windowTarget = block.querySelector('[data-swaglab-delivery-window]');
    const locationTarget = block.querySelector('[data-swaglab-delivery-location]');
    const destination = formatCartDestination(block.dataset.country?.trim());

    if (windowTarget) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
      });
      const sameMonth = deliveryFrom.getFullYear() === deliveryTo.getFullYear()
        && deliveryFrom.getMonth() === deliveryTo.getMonth();
      const formattedWindow = sameMonth
        ? `${formatter.format(deliveryFrom)}–${deliveryTo.getDate()}`
        : `${formatter.format(deliveryFrom)} – ${formatter.format(deliveryTo)}`;
      if (windowTarget.textContent !== formattedWindow) windowTarget.textContent = formattedWindow;
    }

    if (locationTarget) {
      const locationText = destination || 'your location';
      if (locationTarget.textContent !== locationText) locationTarget.textContent = locationText;
    }
    block.dataset.swaglabTrustReady = 'true';
  });
}

function parseJudgeMeNumber(value) {
  const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
  return match ? Number.parseFloat(match[0]) : Number.NaN;
}

function readJudgeMeProductRating() {
  const widget = document.querySelector('#judgeme_product_reviews');
  if (!widget) return null;

  const ratingText = widget.querySelector(
    '.jm-average-rating-display .jm-text:first-of-type, .jdgm-rev-widg__summary-average'
  )?.textContent;
  const countText = widget.querySelector(
    '.jm-average-rating-display .jm-text:last-of-type, .jdgm-rev-widg__summary-text'
  )?.textContent;
  const rating = parseJudgeMeNumber(ratingText);
  const count = Math.round(parseJudgeMeNumber(countText));

  if (!Number.isFinite(rating) || !Number.isFinite(count) || rating <= 0 || count <= 0) return null;
  return { rating: Math.min(5, rating), count };
}

function prepareProductRatingSummary(root = document) {
  const closestSummary = root.closest?.('[data-swaglab-product-rating-summary]');
  const summaries = root.matches?.('[data-swaglab-product-rating-summary]')
    ? [root]
    : closestSummary
      ? [closestSummary]
      : [...root.querySelectorAll('[data-swaglab-product-rating-summary]')];
  if (!summaries.length) return;

  const judgeMeRating = readJudgeMeProductRating();
  summaries.forEach((summary) => {
    const liquidRating = Number.parseFloat(summary.dataset.swaglabRating || '');
    const liquidCount = Number.parseInt(summary.dataset.swaglabReviewCount || '', 10);
    const ratingData = judgeMeRating
      || (Number.isFinite(liquidRating) && Number.isFinite(liquidCount) && liquidRating > 0 && liquidCount > 0
        ? { rating: Math.min(5, liquidRating), count: liquidCount }
        : null);

    if (!ratingData) {
      summary.hidden = true;
      return;
    }

    const ratingLabel = ratingData.rating.toFixed(1);
    const countLabel = `(${ratingData.count} ${ratingData.count === 1 ? 'review' : 'reviews'})`;
    const ariaLabel = `Rated ${ratingLabel} out of 5 from ${ratingData.count} ${ratingData.count === 1 ? 'review' : 'reviews'}`;
    const valueTarget = summary.querySelector('[data-swaglab-rating-value]');
    const countTarget = summary.querySelector('[data-swaglab-rating-count]');

    summary.style.setProperty('--swaglab-rating-percent', `${(ratingData.rating / 5) * 100}%`);
    if (valueTarget?.textContent !== ratingLabel) valueTarget.textContent = ratingLabel;
    if (countTarget?.textContent !== countLabel) countTarget.textContent = countLabel;
    if (summary.getAttribute('aria-label') !== ariaLabel) summary.setAttribute('aria-label', ariaLabel);
    summary.hidden = false;
  });
}

document.addEventListener('click', (event) => {
  const ratingSummary = event.target.closest('[data-swaglab-product-rating-summary]');
  if (ratingSummary) {
    const reviews = document.querySelector('#judgeme_product_reviews');
    if (reviews) {
      event.preventDefault();
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return;
  }

  const mainMenuTrigger = event.target.closest('[data-swaglab-open-main-menu]');
  if (mainMenuTrigger) {
    const summaries = [...document.querySelectorAll('header-drawer summary.header__icon--menu')];
    const summary = summaries.find((element) => element.offsetParent !== null) || summaries[0];
    const details = summary?.closest('details');
    const openMainMenu = () => {
      if (summary && !details?.open) summary.click();
    };

    if (customElements.get('header-drawer')) {
      openMainMenu();
    } else {
      customElements.whenDefined('header-drawer').then(openMainMenu);
    }
    return;
  }

  const unitButton = event.target.closest('.size-unit-btn');
  if (unitButton) {
    updateSizeChartUnit(unitButton);
    return;
  }

  const closeButton = event.target.closest('[data-swaglab-size-guide-close]');
  if (closeButton) {
    closeSizeGuide(closeButton.closest('[data-swaglab-size-guide]'));
    return;
  }

  const link = event.target.closest('[data-swaglab-size-chart-link]');
  if (!link) return;

  const chartSelector = link.getAttribute('href');
  const chart = (chartSelector ? document.querySelector(chartSelector) : null) || document.querySelector('.size-chart-wrapper');
  if (!chart) return;

  event.preventDefault();
  openSizeGuide(chart, link);
});

document.addEventListener('cancel', (event) => {
  const dialog = event.target.closest?.('[data-swaglab-size-guide]');
  if (!dialog) return;
  event.preventDefault();
  closeSizeGuide(dialog);
}, true);

document.addEventListener('click', (event) => {
  const dialog = event.target.closest?.('[data-swaglab-size-guide]');
  if (dialog && event.target === dialog) closeSizeGuide(dialog);
});

function prepareProductEnhancements(root = document) {
  if (!document.querySelector('main[data-template="product"]')) return;

  prepareSizeChartLinks(root);
  prepareProductGalleryPagination(root);
  prepareProductLightbox(root);
  prepareProductRatingSummary(root);
  prepareProductOrigin(root);
}

function startCartTrustObserver() {
  const cartRoot = document.querySelector('cart-drawer-component');
  if (!cartRoot || cartRoot.dataset.swaglabTrustObserverReady === 'true') return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.target instanceof Element) prepareCartTrust(mutation.target);
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) prepareCartTrust(node);
      });
    });
  });

  observer.observe(cartRoot, { childList: true, subtree: true });
  cartRoot.dataset.swaglabTrustObserverReady = 'true';
  cartRoot.swaglabTrustObserver = observer;
}

function startProductEnhancementObserver() {
  const productMain = document.querySelector('main[data-template="product"]');
  if (!productMain || productMain.dataset.swaglabEnhancementObserverReady === 'true') return;

  let animationFrame = 0;
  const refresh = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = 0;
      prepareProductEnhancements(productMain);
    });
  };
  const observer = new MutationObserver(refresh);

  observer.observe(productMain, {
    childList: true,
    subtree: true,
    characterData: true,
  });
  productMain.dataset.swaglabEnhancementObserverReady = 'true';
  productMain.swaglabEnhancementObserver = observer;
}

function initializeSwaglabEnhancements() {
  rememberCollectionOrigin();
  prepareProductEnhancements();
  prepareCartTrust();
  startCartTrustObserver();
  startProductEnhancementObserver();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSwaglabEnhancements, { once: true });
} else {
  initializeSwaglabEnhancements();
}

document.addEventListener('shopify:section:load', (event) => {
  prepareProductEnhancements(event.target);
  prepareCartTrust(event.target);
  startCartTrustObserver();
  startProductEnhancementObserver();
});
